import { useEffect, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import Tooltip from "../Tooltip";
import { getBadgeClassByPolicyStatus } from "js/formatting";

export default function RELEASING_AutoCompleteComboBox({
  labelName = "Quick Filter",
  tooltipVerbiage,
  inputPlaceholderText = "Search by keyword",
  options = [],
  invalidMessage = "Nothing Found",
  selectedOption,
  setSelectedOption,
  readOnly = false,
  useSmallFont = false,
  isPagination = false,
  isChat = false,
  showBadge = false,
}) {
  const [selected, setSelected] = useState(selectedOption);
  const [query, setQuery] = useState("");
  const [showOption, setShowOption] = useState(false);

  // Filter options based on the user's query
  const filteredOptions = useFilteredOptions(query, options);

  // Update the selected state when options or selectedOption change
  useEffect(() => {
    const findSelectedOption = () => {
      return options.find(
        (option) =>
          option.value ===
          (typeof selectedOption === "object"
            ? selectedOption.value
            : selectedOption)
      );
    };
    setSelected(findSelectedOption());
  }, [options, selectedOption]);

  // Clear input field when clicked
  const handleInputClick = (e) => {
    if (e.target.value !== "") setShowOption((option) => !option);
    if (!isPagination) e.target.value = "";
  };

  // Re-apply selected value on input blur
  const handleInputBlur = (e, activeOption) => {
    e.target.value =
      selected?.selectedPolicyDisplayAs ?? options[0]?.selectedPolicyDisplayAs;
    if (
      (!activeOption?.value || activeOption?.value === selected?.value) &&
      !activeOption?.isInActive
    ) {
      setShowOption(false);
    }
  };

  return (
    <Combobox
      value={selected}
      onChange={(value) => {
        setSelectedOption(value);
        setSelected(value);
        setShowOption(false);
      }}
      disabled={readOnly}
    >
      {({ activeIndex, activeOption }) => (
        <div className="relative">
          {renderLabel(labelName, tooltipVerbiage)}
          <div className="relative">
            <Combobox.Input
              placeholder={inputPlaceholderText}
              onClick={handleInputClick}
              onBlur={(e) => handleInputBlur(e, activeOption)}
              displayValue={(option) => option?.selectedPolicyDisplayAs}
              readOnly
              disabled
              className={`cursor-default ${isChat ? "pr-10" : ""}`}
            />
            {!readOnly && (
              <span className="comboBox-caret">
                {showOption ? (
                  <ChevronUpIcon
                    className="h-5 w-5 text-blue-400"
                    aria-hidden="true"
                  />
                ) : (
                  <ChevronDownIcon
                    className="h-5 w-5 text-blue-400"
                    aria-hidden="true"
                  />
                )}
              </span>
            )}
          </div>
          <Transition
            show={showOption}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            {renderOptions(
              filteredOptions,
              invalidMessage,
              query,
              useSmallFont,
              showBadge
            )}
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

// Custom hook to filter options
const useFilteredOptions = (query, options) => {
  return query === ""
    ? options
    : options.filter((option) =>
        option.displayAs
          .toLowerCase()
          .replace(/\s+/g, "")
          .includes(query.toLowerCase().replace(/\s+/g, ""))
      );
};

// Renders the label and tooltip if provided
const renderLabel = (labelName, tooltipVerbiage) =>
  labelName && (
    <div className="flex items-center mb-1">
      <Combobox.Label>{labelName}</Combobox.Label>
      {tooltipVerbiage && <Tooltip xMargin="mx-0" content={tooltipVerbiage} />}
    </div>
  );

const getBadgeIcon = (policy, useSmallFont) => {
  const iconSize = useSmallFont ? 16 : 20;
  const badgeClass = getBadgeClassByPolicyStatus(policy.policyData);
  const iconMap = {
    good: "#ui-icon-active",
    risk: "#ui-icon-grace",
    problem: "#ui-icon-inactive",
  };
  return (
    <svg
      className="badge-icon"
      width={iconSize}
      height={iconSize}
      viewBox="0 0 20 20"
      fill="none"
    >
      <use href={iconMap[badgeClass]}></use>
    </svg>
  );
};

// Renders option items within the Combobox
const renderOptions = (
  filteredOptions,
  invalidMessage,
  query,
  useSmallFont,
  showBadge
) => (
  <Combobox.Options
    static
    className="absolute mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg sm:text-sm z-10"
  >
    {filteredOptions.length === 0 && query !== "" ? (
      <div className="relative cursor-default select-none py-2 px-4">
        {invalidMessage}
      </div>
    ) : (
      <>
        {filteredOptions.map((option, index) =>
          option?.displayAs ? (
            <div key={`${option.value}-${index}`}>
              <>
                <Combobox.Option
                  className={({ active }) =>
                    `relative cursor-default select-none px-2 py-2`
                  }
                  value={option}
                  disabled={option?.isInActive}
                >
                  {({ selected, active }) => (
                    <div
                      className={`pl-2 ${
                        selected
                          ? "bg-ui-blue text-white rounded-sm"
                          : active
                          ? "bg-ui-blue-light text-body rounded-sm"
                          : "text-body"
                      }`}
                    >
                      <span className="block truncate">
                        <div className="flex items-center gap-1">
                          {option.value !== "" &&
                          option.value !== "all" &&
                          !option.isDisplayOnly ? (
                            <>
                              <span
                                className={`font-semibold ${
                                  useSmallFont ? "text-xs" : "text-sm"
                                } ${
                                  option?.isInActive
                                    ? "text-[color:var(--ui-gray-light)]"
                                    : ""
                                }`}
                              >
                                {option.prodType}
                                {option.prodType ? ": " : ""}
                              </span>
                              <span
                                className={`font-normal ${
                                  useSmallFont ? "text-xs" : "text-sm"
                                } ${
                                  option?.isInActive
                                    ? "text-[color:var(--ui-gray-light)]"
                                    : ""
                                }`}
                              >
                                {option.value}
                              </span>
                              {showBadge && option?.status && (
                                <span className="ml-auto pr-2">
                                  {getBadgeIcon(option, useSmallFont)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span
                              className={`font-semibold ${
                                useSmallFont ? "text-xs" : "text-sm"
                              } `}
                            >
                              {option.displayAs}
                            </span>
                          )}
                        </div>
                        {!option.isDisplayOnly && (
                          <div
                            className={`font-semibold ${
                              useSmallFont ? "text-xxs" : "text-xs"
                            } pt-1 ${
                              option?.isInActive
                                ? "text-[color:var(--ui-gray-light)]"
                                : ""
                            }`}
                          >
                            {option.insuredName}
                          </div>
                        )}
                      </span>
                    </div>
                  )}
                </Combobox.Option>
                {index + 1 !== filteredOptions.length && (
                  <hr className="ui-combo-box" />
                )}
              </>
            </div>
          ) : null
        )}
      </>
    )}
  </Combobox.Options>
);
